// Plating and shoot guide cards (M16). A card is a simple drawn layout of a plate, seen from above (or from
// the side for drinks and burgers): where the main item, sides, sauce and garnish go, where to leave empty
// space, the plate, the best camera angle and a lighting tip, plus 3–4 short plating steps — English and
// Spanish (usted). Positions are fractions of the drawing area (0..1), so cards scale to any size.
import type { Bi } from '../../design/model/types';

export type PlateKind = 'round' | 'bowl' | 'board' | 'slate' | 'basket' | 'glass' | 'mug' | 'tray' | 'cone';
export type ElementKind = 'main' | 'side' | 'sauce' | 'garnish' | 'drink' | 'bread' | 'space';
export type ElementShape = 'circle' | 'ellipse' | 'rect' | 'leaf' | 'drop' | 'triangle' | 'stack';
export type PlateElement = {
  id: string;
  kind: ElementKind;
  shape: ElementShape;
  /** Centre and size as fractions of the drawing area; rot in degrees. */
  x: number;
  y: number;
  w: number;
  h: number;
  rot: number;
  color: string;
  label: Bi;
};
export type CameraAngle = 'overhead' | '45' | 'side';
export type PlatingCard = {
  id: string;
  builtIn: boolean;
  name: Bi;
  plate: PlateKind;
  view: 'top' | 'side';
  angle: CameraAngle;
  elements: PlateElement[];
  light: Bi;
  steps: Bi[];
  /** Own cards: a reference photo of the real dish (JPEG/PNG bytes). */
  photo?: { bytes: Uint8Array; type: string } | null;
  updatedAt?: string;
};

const bi = (en: string, es: string): Bi => ({ en, es });
let n = 0;
const el = (
  kind: ElementKind,
  shape: ElementShape,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
  label: Bi,
  rot = 0,
): PlateElement => ({ id: `e${++n}`, kind, shape, x, y, w, h, rot, color, label });
const space = (x: number, y: number, w: number, h: number) =>
  el('space', 'ellipse', x, y, w, h, '#ffffff', bi('Leave empty', 'Dejar vacío'));

const C = {
  meat: '#8b4a2b',
  chicken: '#d9a35b',
  rice: '#f1e6c8',
  green: '#4f9a3a',
  red: '#c0392b',
  yellow: '#f2c230',
  orange: '#e67e22',
  brown: '#6e4a2f',
  cream: '#f5e9d3',
  bread: '#d8a25e',
  pink: '#e8a0a8',
  purple: '#7d3c98',
  fish: '#f0b9a0',
  coffee: '#5b3a24',
  white: '#fafafa',
};

const LIGHT = {
  side: bi(
    'Soft window light from the side (left or right), no flash. A white card on the other side lifts the shadows.',
    'Luz suave de ventana de lado (izquierda o derecha), sin flash. Una cartulina blanca del otro lado aclara las sombras.',
  ),
  back: bi(
    'Light from behind and a little to the side makes sauces, broth and drinks shine. Avoid light from the camera side.',
    'La luz desde atrás y un poco de lado hace brillar salsas, caldos y bebidas. Evite la luz del lado de la cámara.',
  ),
  overhead: bi(
    'Even, soft light from a big window; turn off the ceiling lights so there are no yellow spots.',
    'Luz pareja y suave de una ventana grande; apague las luces del techo para evitar manchas amarillas.',
  ),
};

export const BUILT_IN_CARDS: PlatingCard[] = [
  {
    id: 'flat-plate',
    builtIn: true,
    name: bi('Main dish on a flat plate', 'Plato fuerte en plato plano'),
    plate: 'round',
    view: 'top',
    angle: '45',
    elements: [
      el('main', 'ellipse', 0.42, 0.5, 0.36, 0.26, C.meat, bi('Main (protein)', 'Principal (proteína)'), -15),
      el('side', 'circle', 0.66, 0.36, 0.2, 0.2, C.rice, bi('Starch side', 'Acompañamiento de harina')),
      el('side', 'ellipse', 0.66, 0.66, 0.2, 0.15, C.green, bi('Vegetables', 'Verduras'), 20),
      el(
        'sauce',
        'drop',
        0.36,
        0.7,
        0.14,
        0.08,
        C.red,
        bi('Sauce: a line, not a puddle', 'Salsa: una línea, no un charco'),
        -20,
      ),
      el('garnish', 'leaf', 0.48, 0.42, 0.06, 0.04, C.green, bi('Fresh herb on top', 'Hierba fresca encima')),
      space(0.28, 0.3, 0.14, 0.1),
    ],
    light: LIGHT.side,
    steps: [
      bi(
        'Wipe the rim clean; keep food inside the inner circle.',
        'Limpie el borde; deje la comida dentro del círculo interior.',
      ),
      bi(
        'Main a little off centre, pointing at the camera.',
        'Principal un poco fuera del centro, apuntando a la cámara.',
      ),
      bi(
        'Sides behind the main so they show above it at 45°.',
        'Acompañamientos detrás del principal para que se vean a 45°.',
      ),
      bi('Sauce last, in a line or dots; herb on top.', 'Salsa al final, en línea o puntos; hierba encima.'),
    ],
  },
  {
    id: 'bowl',
    builtIn: true,
    name: bi('Bowl (rice bowl, poke)', 'Bowl (arroz, poke)'),
    plate: 'bowl',
    view: 'top',
    angle: 'overhead',
    elements: [
      el(
        'side',
        'ellipse',
        0.5,
        0.62,
        0.42,
        0.24,
        C.rice,
        bi('Base (rice / grains)', 'Base (arroz / granos)'),
      ),
      el(
        'main',
        'rect',
        0.38,
        0.4,
        0.2,
        0.16,
        C.chicken,
        bi('Protein in one wedge', 'Proteína en una sección'),
        -25,
      ),
      el('side', 'ellipse', 0.62, 0.38, 0.18, 0.14, C.green, bi('Greens', 'Verdes'), 15),
      el(
        'side',
        'circle',
        0.66,
        0.6,
        0.13,
        0.13,
        C.orange,
        bi('Colour side (carrot, mango)', 'Toque de color (zanahoria, mango)'),
      ),
      el('sauce', 'drop', 0.5, 0.5, 0.12, 0.06, C.red, bi('Sauce zig-zag', 'Salsa en zigzag'), 10),
      el('garnish', 'circle', 0.46, 0.46, 0.05, 0.05, C.yellow, bi('Sesame / lime', 'Ajonjolí / limón')),
    ],
    light: LIGHT.overhead,
    steps: [
      bi('Fill the bowl to just below the rim.', 'Llene el bowl hasta justo debajo del borde.'),
      bi(
        'Lay each topping in its own wedge, like a clock.',
        'Ponga cada ingrediente en su propia sección, como un reloj.',
      ),
      bi('Put contrasting colours next to each other.', 'Ponga colores que contrastan uno al lado del otro.'),
      bi(
        'Sauce and seeds last, right before the photo.',
        'Salsa y semillas al final, justo antes de la foto.',
      ),
    ],
  },
  {
    id: 'burger',
    builtIn: true,
    name: bi('Burger', 'Hamburguesa'),
    plate: 'board',
    view: 'side',
    angle: 'side',
    elements: [
      el(
        'bread',
        'ellipse',
        0.42,
        0.34,
        0.34,
        0.12,
        C.bread,
        bi('Top bun, seeds up', 'Pan de arriba, ajonjolí visible'),
      ),
      el(
        'garnish',
        'rect',
        0.42,
        0.43,
        0.36,
        0.04,
        C.green,
        bi('Lettuce edge showing', 'Borde de lechuga visible'),
      ),
      el(
        'main',
        'rect',
        0.42,
        0.5,
        0.36,
        0.08,
        C.meat,
        bi('Patty, front edge to camera', 'Carne, el borde hacia la cámara'),
      ),
      el(
        'sauce',
        'rect',
        0.42,
        0.56,
        0.34,
        0.03,
        C.yellow,
        bi('Melted cheese drip', 'Queso derretido cayendo'),
      ),
      el('bread', 'ellipse', 0.42, 0.63, 0.34, 0.08, C.bread, bi('Bottom bun', 'Pan de abajo')),
      el(
        'side',
        'stack',
        0.78,
        0.58,
        0.14,
        0.22,
        C.yellow,
        bi('Fries behind, out of focus', 'Papas atrás, desenfocadas'),
      ),
      space(0.15, 0.3, 0.12, 0.3),
    ],
    light: LIGHT.side,
    steps: [
      bi(
        'Build it tall; push fillings to the front edge.',
        'Ármela alta; empuje los ingredientes al borde de adelante.',
      ),
      bi('A skewer in the middle keeps it straight.', 'Un palillo en el centro la mantiene derecha.'),
      bi('Camera at bun height, straight on.', 'Cámara a la altura del pan, de frente.'),
      bi('Fries and drink behind, softer and smaller.', 'Papas y bebida atrás, más suaves y pequeñas.'),
    ],
  },
  {
    id: 'sandwich',
    builtIn: true,
    name: bi('Sandwich cut in half', 'Sándwich cortado a la mitad'),
    plate: 'board',
    view: 'top',
    angle: '45',
    elements: [
      el(
        'main',
        'triangle',
        0.38,
        0.48,
        0.3,
        0.3,
        C.bread,
        bi('Half 1, cut side to camera', 'Mitad 1, corte hacia la cámara'),
        10,
      ),
      el(
        'main',
        'triangle',
        0.58,
        0.44,
        0.3,
        0.3,
        C.bread,
        bi('Half 2, leaning on half 1', 'Mitad 2, apoyada en la mitad 1'),
        -30,
      ),
      el('side', 'ellipse', 0.74, 0.68, 0.18, 0.12, C.yellow, bi('Chips or fries', 'Papalinas o papas')),
      el('sauce', 'circle', 0.28, 0.7, 0.1, 0.1, C.red, bi('Dip in a small cup', 'Aderezo en vasito')),
      space(0.22, 0.26, 0.14, 0.1),
    ],
    light: LIGHT.side,
    steps: [
      bi('Cut diagonally with a sharp, wet knife.', 'Corte en diagonal con un cuchillo afilado y mojado.'),
      bi(
        'Show the cut face: the layers sell the sandwich.',
        'Muestre el corte: las capas venden el sándwich.',
      ),
      bi(
        'Stack the halves slightly, not side by side.',
        'Ponga las mitades un poco encimadas, no lado a lado.',
      ),
    ],
  },
  {
    id: 'tacos',
    builtIn: true,
    name: bi('Tacos (3)', 'Tacos (3)'),
    plate: 'round',
    view: 'top',
    angle: 'overhead',
    elements: [
      el('main', 'ellipse', 0.35, 0.5, 0.18, 0.36, C.chicken, bi('Taco 1', 'Taco 1'), 8),
      el('main', 'ellipse', 0.5, 0.5, 0.18, 0.36, C.meat, bi('Taco 2', 'Taco 2'), 8),
      el('main', 'ellipse', 0.65, 0.5, 0.18, 0.36, C.chicken, bi('Taco 3', 'Taco 3'), 8),
      el(
        'garnish',
        'circle',
        0.5,
        0.45,
        0.08,
        0.08,
        C.green,
        bi('Onion + cilantro on every taco', 'Cebolla y cilantro en cada taco'),
      ),
      el('garnish', 'triangle', 0.78, 0.28, 0.1, 0.08, C.green, bi('Lime wedges', 'Gajos de limón')),
      el(
        'sauce',
        'circle',
        0.24,
        0.24,
        0.1,
        0.1,
        C.red,
        bi('Salsa in a small bowl', 'Salsa en un tazoncito'),
      ),
    ],
    light: LIGHT.overhead,
    steps: [
      bi('Line the tacos up, all at the same angle.', 'Alinee los tacos, todos en el mismo ángulo.'),
      bi(
        'Fill generously so the filling shows above the tortilla.',
        'Rellene bien para que el relleno se vea sobre la tortilla.',
      ),
      bi('Toppings in the same order on each taco.', 'Ingredientes en el mismo orden en cada taco.'),
    ],
  },
  {
    id: 'salad',
    builtIn: true,
    name: bi('Salad', 'Ensalada'),
    plate: 'bowl',
    view: 'top',
    angle: '45',
    elements: [
      el(
        'side',
        'ellipse',
        0.5,
        0.55,
        0.5,
        0.36,
        C.green,
        bi('Leaves with height, not flat', 'Hojas con volumen, no planas'),
      ),
      el('main', 'rect', 0.45, 0.45, 0.18, 0.12, C.chicken, bi('Protein on top', 'Proteína encima'), -20),
      el(
        'garnish',
        'circle',
        0.62,
        0.52,
        0.08,
        0.08,
        C.red,
        bi('Tomatoes, cut side up', 'Tomates, corte hacia arriba'),
      ),
      el('garnish', 'circle', 0.36, 0.6, 0.07, 0.07, C.yellow, bi('Croutons / corn', 'Crutones / elote')),
      el('sauce', 'circle', 0.82, 0.24, 0.1, 0.1, C.cream, bi('Dressing on the side', 'Aderezo aparte')),
    ],
    light: LIGHT.side,
    steps: [
      bi('Toss the leaves gently and pile them up.', 'Mezcle las hojas con cuidado y apílelas.'),
      bi(
        'Tuck the colourful items in, not all on top.',
        'Acomode lo colorido entre las hojas, no todo encima.',
      ),
      bi(
        'Dress at the last second, or serve it on the side.',
        'Ponga el aderezo al último segundo, o sírvalo aparte.',
      ),
    ],
  },
  {
    id: 'pasta',
    builtIn: true,
    name: bi('Pasta', 'Pasta'),
    plate: 'bowl',
    view: 'top',
    angle: '45',
    elements: [
      el(
        'main',
        'circle',
        0.5,
        0.52,
        0.36,
        0.36,
        C.cream,
        bi('Twirled nest of pasta', 'Nido de pasta enrollada'),
      ),
      el(
        'sauce',
        'drop',
        0.5,
        0.5,
        0.18,
        0.1,
        C.red,
        bi('Sauce on top, not drowning', 'Salsa encima, sin ahogarla'),
      ),
      el('garnish', 'leaf', 0.56, 0.42, 0.07, 0.05, C.green, bi('Basil / parsley', 'Albahaca / perejil')),
      el('garnish', 'circle', 0.42, 0.45, 0.05, 0.05, C.yellow, bi('Grated cheese', 'Queso rallado')),
      space(0.25, 0.25, 0.14, 0.12),
    ],
    light: LIGHT.side,
    steps: [
      bi(
        'Twirl the pasta with tongs in a ladle to make a tall nest.',
        'Enrolle la pasta con pinzas en un cucharón para hacer un nido alto.',
      ),
      bi('Spoon a little sauce on top; wipe the bowl.', 'Ponga un poco de salsa encima; limpie el plato.'),
      bi(
        'Cheese and herb last; shoot fast before it settles.',
        'Queso y hierba al final; fotografíe rápido antes de que se asiente.',
      ),
    ],
  },
  {
    id: 'soup',
    builtIn: true,
    name: bi('Soup / broth (caldo)', 'Sopa / caldo'),
    plate: 'bowl',
    view: 'top',
    angle: '45',
    elements: [
      el(
        'main',
        'circle',
        0.5,
        0.5,
        0.44,
        0.44,
        C.orange,
        bi('Broth to 1 cm below the rim', 'Caldo a 1 cm del borde'),
      ),
      el('side', 'ellipse', 0.44, 0.46, 0.16, 0.1, C.chicken, bi('Pieces peeking out', 'Piezas asomándose')),
      el('garnish', 'leaf', 0.58, 0.44, 0.06, 0.04, C.green, bi('Cilantro on top', 'Cilantro encima')),
      el(
        'side',
        'ellipse',
        0.82,
        0.74,
        0.16,
        0.12,
        C.bread,
        bi('Tortillas / bread beside', 'Tortillas / pan al lado'),
      ),
      el('garnish', 'triangle', 0.2, 0.75, 0.08, 0.06, C.green, bi('Lime', 'Limón')),
    ],
    light: LIGHT.back,
    steps: [
      bi(
        'Put the solid pieces in first so they rise above the broth.',
        'Ponga primero las piezas sólidas para que sobresalgan del caldo.',
      ),
      bi(
        'Pour the broth carefully; no splashes on the rim.',
        'Sirva el caldo con cuidado; sin gotas en el borde.',
      ),
      bi(
        'Shoot with the light behind: the steam and shine show.',
        'Fotografíe con la luz atrás: se ve el vapor y el brillo.',
      ),
    ],
  },
  {
    id: 'dessert',
    builtIn: true,
    name: bi('Dessert slice', 'Postre en rebanada'),
    plate: 'round',
    view: 'top',
    angle: '45',
    elements: [
      el(
        'main',
        'triangle',
        0.46,
        0.52,
        0.32,
        0.3,
        C.coffee,
        bi('Slice, tip to the camera', 'Rebanada, la punta hacia la cámara'),
        180,
      ),
      el('sauce', 'drop', 0.66, 0.66, 0.16, 0.06, C.red, bi('Sauce swoosh', 'Trazo de salsa'), -30),
      el('garnish', 'circle', 0.62, 0.36, 0.08, 0.08, C.pink, bi('Berries', 'Frutos rojos')),
      el('garnish', 'leaf', 0.66, 0.34, 0.05, 0.03, C.green, bi('Mint leaf', 'Hoja de menta')),
      space(0.28, 0.3, 0.16, 0.12),
    ],
    light: LIGHT.back,
    steps: [
      bi(
        'Cut with a hot, clean knife for a sharp edge.',
        'Corte con un cuchillo caliente y limpio para un borde nítido.',
      ),
      bi('Lay the slice on its side so the layers show.', 'Acueste la rebanada para que se vean las capas.'),
      bi('Leave a third of the plate empty.', 'Deje vacío un tercio del plato.'),
    ],
  },
  {
    id: 'drinks',
    builtIn: true,
    name: bi('Drinks (lemonade, licuado)', 'Bebidas (limonada, licuado)'),
    plate: 'glass',
    view: 'side',
    angle: 'side',
    elements: [
      el(
        'drink',
        'rect',
        0.45,
        0.55,
        0.26,
        0.44,
        C.yellow,
        bi('Glass filled almost to the top', 'Vaso lleno casi hasta arriba'),
      ),
      el(
        'garnish',
        'circle',
        0.56,
        0.32,
        0.12,
        0.12,
        C.green,
        bi('Fruit slice on the rim', 'Rodaja de fruta en el borde'),
      ),
      el('garnish', 'rect', 0.4, 0.28, 0.03, 0.2, C.white, bi('Straw', 'Pajilla'), 10),
      el('side', 'circle', 0.78, 0.74, 0.12, 0.12, C.green, bi('Fresh fruit beside', 'Fruta fresca al lado')),
      space(0.18, 0.4, 0.12, 0.3),
    ],
    light: LIGHT.back,
    steps: [
      bi('Use a clean, dry glass; wipe fingerprints.', 'Use un vaso limpio y seco; quite las huellas.'),
      bi(
        'Ice first, then the drink: it stays bright.',
        'Primero el hielo, luego la bebida: se ve más brillante.',
      ),
      bi('Light from behind makes the drink glow.', 'La luz desde atrás hace brillar la bebida.'),
    ],
  },
  {
    id: 'breakfast',
    builtIn: true,
    name: bi('Breakfast plate (chapín)', 'Desayuno chapín'),
    plate: 'round',
    view: 'top',
    angle: 'overhead',
    elements: [
      el('main', 'circle', 0.4, 0.4, 0.2, 0.2, C.yellow, bi('Eggs', 'Huevos')),
      el('side', 'ellipse', 0.62, 0.42, 0.22, 0.14, C.brown, bi('Black beans', 'Frijoles volteados')),
      el('side', 'ellipse', 0.42, 0.66, 0.24, 0.12, C.yellow, bi('Plantains', 'Plátanos fritos'), -10),
      el('side', 'rect', 0.64, 0.64, 0.14, 0.1, C.white, bi('Fresh cheese', 'Queso fresco')),
      el('sauce', 'circle', 0.52, 0.52, 0.07, 0.07, C.cream, bi('Cream', 'Crema')),
      el(
        'bread',
        'circle',
        0.84,
        0.24,
        0.14,
        0.14,
        C.bread,
        bi('Tortillas / bread off the plate', 'Tortillas / pan fuera del plato'),
      ),
    ],
    light: LIGHT.overhead,
    steps: [
      bi(
        'Each item in its own area; no overlapping puddles.',
        'Cada cosa en su lugar; sin charcos encimados.',
      ),
      bi(
        'Turn the plantains so the caramel side shows.',
        'Voltee los plátanos para que se vea el lado dorado.',
      ),
      bi('Coffee cup at the edge of the frame.', 'La taza de café al borde de la foto.'),
    ],
  },
  {
    id: 'combo',
    builtIn: true,
    name: bi('Combo platter / tray', 'Combo en bandeja'),
    plate: 'tray',
    view: 'top',
    angle: '45',
    elements: [
      el(
        'main',
        'ellipse',
        0.38,
        0.45,
        0.3,
        0.24,
        C.meat,
        bi('Main item, front left', 'Principal, adelante a la izquierda'),
      ),
      el(
        'side',
        'stack',
        0.66,
        0.42,
        0.18,
        0.22,
        C.yellow,
        bi('Fries in a cone or basket', 'Papas en cono o canasta'),
      ),
      el(
        'drink',
        'circle',
        0.8,
        0.24,
        0.16,
        0.16,
        C.red,
        bi('Drink, back right', 'Bebida, atrás a la derecha'),
      ),
      el('sauce', 'circle', 0.6, 0.7, 0.09, 0.09, C.red, bi('Sauces in cups', 'Salsas en vasitos')),
      el('side', 'circle', 0.3, 0.72, 0.12, 0.12, C.green, bi('Salad / coleslaw', 'Ensalada / repollo')),
    ],
    light: LIGHT.side,
    steps: [
      bi('Biggest item in front, drink at the back.', 'Lo más grande adelante, la bebida atrás.'),
      bi(
        'Leave small gaps so each item reads on its own.',
        'Deje pequeños espacios para que cada cosa se distinga.',
      ),
      bi('Logo cup or wrapper facing the camera.', 'Vaso o envoltorio con logo hacia la cámara.'),
    ],
  },
  {
    id: 'pizza',
    builtIn: true,
    name: bi('Pizza', 'Pizza'),
    plate: 'board',
    view: 'top',
    angle: 'overhead',
    elements: [
      el(
        'main',
        'circle',
        0.48,
        0.5,
        0.6,
        0.6,
        C.orange,
        bi('Whole pizza, slightly off centre', 'Pizza entera, un poco fuera del centro'),
      ),
      el(
        'main',
        'triangle',
        0.82,
        0.74,
        0.2,
        0.18,
        C.orange,
        bi('One slice pulled out (cheese pull)', 'Una porción jalada (queso estirado)'),
        30,
      ),
      el(
        'garnish',
        'leaf',
        0.44,
        0.44,
        0.06,
        0.04,
        C.green,
        bi('Basil after the oven', 'Albahaca al salir del horno'),
      ),
      el('garnish', 'circle', 0.18, 0.2, 0.1, 0.1, C.red, bi('Chili flakes / oil', 'Chile / aceite')),
    ],
    light: LIGHT.overhead,
    steps: [
      bi('Shoot within 2 minutes of the oven.', 'Fotografíe antes de 2 minutos de salir del horno.'),
      bi(
        'Pull one slice out a little to show the cheese.',
        'Saque un poco una porción para mostrar el queso.',
      ),
      bi('Toppings spread evenly to the edge.', 'Ingredientes parejos hasta la orilla.'),
    ],
  },
  {
    id: 'sushi',
    builtIn: true,
    name: bi('Sushi rolls', 'Rollos de sushi'),
    plate: 'slate',
    view: 'top',
    angle: '45',
    elements: [
      el(
        'main',
        'circle',
        0.3,
        0.5,
        0.13,
        0.13,
        C.fish,
        bi('Pieces in a gentle curve', 'Piezas en una curva suave'),
      ),
      el('main', 'circle', 0.44, 0.47, 0.13, 0.13, C.fish, bi('Cut side up', 'Corte hacia arriba')),
      el('main', 'circle', 0.58, 0.47, 0.13, 0.13, C.fish, bi('Same spacing', 'Mismo espacio entre piezas')),
      el('main', 'circle', 0.72, 0.5, 0.13, 0.13, C.fish, bi('Odd number (5, 7)', 'Número impar (5, 7)')),
      el('garnish', 'circle', 0.82, 0.74, 0.08, 0.08, C.green, bi('Wasabi', 'Wasabi')),
      el('garnish', 'ellipse', 0.68, 0.74, 0.1, 0.06, C.pink, bi('Ginger', 'Jengibre')),
      el('sauce', 'circle', 0.2, 0.74, 0.1, 0.1, C.coffee, bi('Soy sauce dish', 'Plato de salsa de soya')),
    ],
    light: LIGHT.side,
    steps: [
      bi(
        'Cut with a wet knife, wiping it after every cut.',
        'Corte con cuchillo mojado, limpiándolo en cada corte.',
      ),
      bi('Odd numbers look better than even ones.', 'Los números impares se ven mejor que los pares.'),
      bi('A dark plate makes rice and fish pop.', 'Un plato oscuro hace resaltar el arroz y el pescado.'),
    ],
  },
  {
    id: 'steak',
    builtIn: true,
    name: bi('Steak (churrasco)', 'Churrasco'),
    plate: 'board',
    view: 'top',
    angle: '45',
    elements: [
      el(
        'main',
        'ellipse',
        0.45,
        0.48,
        0.42,
        0.24,
        C.meat,
        bi('Meat sliced, pink side up', 'Carne en tiras, lado rosado arriba'),
        -10,
      ),
      el(
        'side',
        'ellipse',
        0.72,
        0.68,
        0.18,
        0.12,
        C.green,
        bi('Chimichurri / guacamole', 'Chimichurri / guacamole'),
      ),
      el('side', 'circle', 0.24, 0.72, 0.14, 0.14, C.red, bi('Chirmol', 'Chirmol')),
      el(
        'side',
        'ellipse',
        0.76,
        0.3,
        0.18,
        0.12,
        C.brown,
        bi('Grilled onions / chorizo', 'Cebollín / chorizo'),
      ),
      el('garnish', 'circle', 0.42, 0.38, 0.05, 0.05, C.white, bi('Coarse salt', 'Sal gruesa')),
    ],
    light: LIGHT.side,
    steps: [
      bi(
        'Rest the meat 5 minutes, then slice across the grain.',
        'Deje reposar la carne 5 minutos y córtela contra la fibra.',
      ),
      bi('Fan the slices so the inside shows.', 'Abra las tiras en abanico para que se vea el centro.'),
      bi('A little oil on top just before the photo.', 'Un poco de aceite encima justo antes de la foto.'),
    ],
  },
  {
    id: 'fried-chicken',
    builtIn: true,
    name: bi('Fried chicken', 'Pollo frito'),
    plate: 'basket',
    view: 'top',
    angle: '45',
    elements: [
      el(
        'main',
        'ellipse',
        0.42,
        0.44,
        0.26,
        0.2,
        C.chicken,
        bi('Biggest piece in front', 'La pieza más grande adelante'),
        -20,
      ),
      el(
        'main',
        'ellipse',
        0.58,
        0.36,
        0.22,
        0.17,
        C.chicken,
        bi('Pieces stacked, crust up', 'Piezas apiladas, empanizado arriba'),
        25,
      ),
      el('side', 'stack', 0.72, 0.62, 0.16, 0.2, C.yellow, bi('Fries', 'Papas')),
      el('sauce', 'circle', 0.28, 0.7, 0.1, 0.1, C.red, bi('Dip', 'Salsa')),
      el('garnish', 'triangle', 0.52, 0.66, 0.07, 0.06, C.green, bi('Lemon', 'Limón')),
    ],
    light: LIGHT.side,
    steps: [
      bi('Paper liner in the basket, a little crumpled.', 'Papel en la canasta, un poco arrugado.'),
      bi('Stack the pieces: height looks generous.', 'Apile las piezas: la altura se ve abundante.'),
      bi(
        'Shoot hot; the crust loses its shine in minutes.',
        'Fotografíe caliente; el empanizado pierde brillo en minutos.',
      ),
    ],
  },
  {
    id: 'ceviche',
    builtIn: true,
    name: bi('Ceviche / cóctel', 'Ceviche / cóctel'),
    plate: 'glass',
    view: 'top',
    angle: '45',
    elements: [
      el(
        'main',
        'circle',
        0.5,
        0.48,
        0.42,
        0.42,
        C.fish,
        bi('Seafood mix, heaped', 'Mariscos, en montoncito'),
      ),
      el('garnish', 'circle', 0.44, 0.4, 0.08, 0.08, C.red, bi('Tomato + onion', 'Tomate y cebolla')),
      el('garnish', 'leaf', 0.58, 0.42, 0.06, 0.04, C.green, bi('Cilantro', 'Cilantro')),
      el('garnish', 'ellipse', 0.68, 0.6, 0.12, 0.08, C.green, bi('Avocado slices', 'Rodajas de aguacate')),
      el(
        'side',
        'ellipse',
        0.82,
        0.78,
        0.16,
        0.1,
        C.yellow,
        bi('Crackers / tostadas', 'Galletas / tostadas'),
      ),
      el('garnish', 'triangle', 0.22, 0.24, 0.08, 0.06, C.green, bi('Lime on the rim', 'Limón en el borde')),
    ],
    light: LIGHT.back,
    steps: [
      bi(
        'Drain extra juice so it looks fresh, not soupy.',
        'Escurra el jugo extra para que se vea fresco, no aguado.',
      ),
      bi('Seafood pieces on top, not hidden under onion.', 'Mariscos encima, no escondidos bajo la cebolla.'),
      bi(
        'A cold glass shows little drops: shoot right away.',
        'Un vaso frío muestra gotitas: fotografíe de inmediato.',
      ),
    ],
  },
  {
    id: 'smoothie-bowl',
    builtIn: true,
    name: bi('Smoothie / fruit bowl', 'Bowl de frutas / licuado'),
    plate: 'bowl',
    view: 'top',
    angle: 'overhead',
    elements: [
      el(
        'main',
        'circle',
        0.5,
        0.5,
        0.5,
        0.5,
        C.purple,
        bi('Thick base, smooth top', 'Base espesa, superficie lisa'),
      ),
      el('garnish', 'rect', 0.38, 0.5, 0.06, 0.34, C.yellow, bi('Banana row', 'Fila de banano')),
      el('garnish', 'rect', 0.5, 0.5, 0.06, 0.34, C.red, bi('Berry row', 'Fila de fresas')),
      el('garnish', 'rect', 0.62, 0.5, 0.06, 0.34, C.cream, bi('Granola row', 'Fila de granola')),
      el('garnish', 'leaf', 0.66, 0.34, 0.05, 0.03, C.green, bi('Mint', 'Menta')),
    ],
    light: LIGHT.overhead,
    steps: [
      bi(
        'Make the base thick so toppings do not sink.',
        'Haga la base espesa para que no se hundan los ingredientes.',
      ),
      bi('Toppings in straight rows or a half moon.', 'Ingredientes en filas rectas o en media luna.'),
      bi(
        'Straight from above, the bowl filling most of the frame.',
        'Desde arriba, con el bowl llenando casi toda la foto.',
      ),
    ],
  },
  {
    id: 'coffee-pastry',
    builtIn: true,
    name: bi('Coffee and pastry', 'Café y pan dulce'),
    plate: 'mug',
    view: 'top',
    angle: 'overhead',
    elements: [
      el(
        'drink',
        'circle',
        0.4,
        0.45,
        0.3,
        0.3,
        C.coffee,
        bi('Cup, handle at 4 o’clock', 'Taza, asa hacia las 4'),
      ),
      el('garnish', 'circle', 0.4, 0.45, 0.14, 0.14, C.cream, bi('Foam / latte art', 'Espuma / arte latte')),
      el(
        'main',
        'ellipse',
        0.7,
        0.62,
        0.26,
        0.18,
        C.bread,
        bi('Pastry on a small plate', 'Pan dulce en platito'),
        20,
      ),
      el(
        'garnish',
        'rect',
        0.62,
        0.3,
        0.12,
        0.03,
        C.white,
        bi('Spoon on the saucer', 'Cucharita en el plato'),
      ),
      space(0.2, 0.75, 0.16, 0.12),
    ],
    light: LIGHT.side,
    steps: [
      bi('Pour the milk just before the photo.', 'Sirva la leche justo antes de la foto.'),
      bi(
        'Handle pointing down-right, toward the viewer.',
        'El asa hacia abajo a la derecha, hacia quien mira.',
      ),
      bi('A few crumbs near the pastry look real.', 'Unas migajas junto al pan se ven reales.'),
    ],
  },
  {
    id: 'kids-meal',
    builtIn: true,
    name: bi("Kids' meal", 'Menú infantil'),
    plate: 'tray',
    view: 'top',
    angle: 'overhead',
    elements: [
      el(
        'main',
        'rect',
        0.36,
        0.42,
        0.26,
        0.2,
        C.chicken,
        bi('Nuggets / mini burger', 'Nuggets / mini hamburguesa'),
      ),
      el('side', 'stack', 0.64, 0.42, 0.16, 0.2, C.yellow, bi('Small fries', 'Papas pequeñas')),
      el('side', 'circle', 0.34, 0.72, 0.14, 0.14, C.orange, bi('Fruit cup', 'Vaso de fruta')),
      el('drink', 'circle', 0.66, 0.72, 0.13, 0.13, C.red, bi('Juice box', 'Jugo')),
      el('garnish', 'circle', 0.82, 0.2, 0.1, 0.1, C.purple, bi('Toy / sticker', 'Juguete / calcomanía')),
    ],
    light: LIGHT.overhead,
    steps: [
      bi('Bright colours, everything easy to recognise.', 'Colores alegres, todo fácil de reconocer.'),
      bi(
        'Leave the toy in view: it is what parents look for.',
        'Deje el juguete a la vista: es lo que buscan los papás.',
      ),
      bi('Straight from above, items in a neat grid.', 'Desde arriba, todo ordenado en cuadrícula.'),
    ],
  },
  {
    id: 'fries-basket',
    builtIn: true,
    name: bi('Fries / snacks basket', 'Canasta de papas / boquitas'),
    plate: 'basket',
    view: 'top',
    angle: '45',
    elements: [
      el(
        'main',
        'stack',
        0.48,
        0.48,
        0.36,
        0.36,
        C.yellow,
        bi('Fries standing, not lying flat', 'Papas paradas, no acostadas'),
      ),
      el('sauce', 'circle', 0.76, 0.66, 0.12, 0.12, C.red, bi('Ketchup', 'Salsa de tomate')),
      el('sauce', 'circle', 0.76, 0.38, 0.12, 0.12, C.yellow, bi('Mayo / cheese', 'Mayonesa / queso')),
      el(
        'garnish',
        'circle',
        0.4,
        0.38,
        0.06,
        0.06,
        C.green,
        bi('Parsley / salt on top', 'Perejil / sal encima'),
      ),
    ],
    light: LIGHT.side,
    steps: [
      bi('Fry, salt and shoot within 3 minutes.', 'Fría, sale y fotografíe antes de 3 minutos.'),
      bi('Prop some fries upright for height.', 'Pare algunas papas para dar altura.'),
      bi(
        'Sauces in cups, never on the fries for the photo.',
        'Salsas en vasitos, nunca sobre las papas para la foto.',
      ),
    ],
  },
];

export const ANGLE_TEXT: Record<CameraAngle, Bi> = {
  overhead: bi('Camera straight above (90°)', 'Cámara justo encima (90°)'),
  '45': bi('Camera at 45°, like sitting at the table', 'Cámara a 45°, como sentado a la mesa'),
  side: bi('Camera at plate height (0–15°)', 'Cámara a la altura del plato (0–15°)'),
};
export const PLATE_TEXT: Record<PlateKind, Bi> = {
  round: bi('Round flat plate', 'Plato plano redondo'),
  bowl: bi('Wide bowl', 'Tazón ancho'),
  board: bi('Wooden board', 'Tabla de madera'),
  slate: bi('Dark slate / plate', 'Pizarra / plato oscuro'),
  basket: bi('Basket with paper', 'Canasta con papel'),
  glass: bi('Clear glass', 'Vaso o copa de vidrio'),
  mug: bi('Cup and saucer', 'Taza y plato'),
  tray: bi('Tray', 'Bandeja'),
  cone: bi('Paper cone', 'Cono de papel'),
};
export const KIND_TEXT: Record<ElementKind, Bi> = {
  main: bi('Main', 'Principal'),
  side: bi('Side', 'Acompañamiento'),
  sauce: bi('Sauce', 'Salsa'),
  garnish: bi('Garnish', 'Decoración'),
  drink: bi('Drink', 'Bebida'),
  bread: bi('Bread', 'Pan'),
  space: bi('Empty space', 'Espacio vacío'),
};
